import React, { useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import IbPopover from '../common/IbPopover';
import IbButton from '../common/IbButton';
import IbIcon from '../common/IbIcon';
import { defaultApi } from '../../apis';
import {
  AgentRunModel,
  DataInterchangeFormat,
  DataInterfaceInput,
  DataPayload,
  DataPayloadType,
  SortDirection,
} from '../../../gamma-api';
import { alertsSelectorAdd } from '../../recoil';
import { getPayloadsTextContent } from '../../utils/messageUtil';
import IbTooltip from '../common/IbTooltip';
import IbTypography from '../common/IbTypography';

import {
  ATTACHMENTS_CLASS_NAME,
  ATTACHMENTS_ITEM_CLASS_NAME,
  ATTACHMENTS_FILE_ITEM_CLASS_NAME,
  ATTACHMENTS_RESOURCE_ITEM_CLASS_NAME,
  MAIN_CLASS_NAME,
  POPOVER_CLASS_NAME,
  RUNS_CLASS_NAME,
  RUNS_ITEM_CLASS_NAME,
  RUNS_PAGE_SIZE,
  SKELETON_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
  SKELETON_COUNT,
} from './const';

interface IRunRetryButtonProps {
  agentId: string;
  dataInterface: DataInterfaceInput;
  onRetry?: (run: AgentRunModel) => void;
}

const RunRetryButton: React.FC<IRunRetryButtonProps> = ({ agentId, dataInterface, onRetry }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const { t } = useTranslation();

  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [latestRun, setLatestRun] = useState<AgentRunModel>();
  const [runList, setRunList] = useState<AgentRunModel[]>([]);

  const loadRuns = async (count: number) => {
    if (!agentId) return;
    try {
      setLoading(true);
      const response = await defaultApi.searchAgentAllRuns(
        [agentId],
        null,
        null,
        null,
        null,
        SortDirection.Desc,
        0,
        count
      );
      return response.data.items;
    } catch (e) {
      addAlert({ content: t('Failed to load runs'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadLatestRun = async () => {
    const runs = await loadRuns(1);
    if (runs?.length) {
      setLatestRun(runs[0]);
    }
  };
  useEffect(() => void loadLatestRun(), []);

  const onRetryClick = async () => {
    const runs = await loadRuns(1);
    if (runs?.length) {
      onRetry?.(runs[0]);
    }
  };

  const onShowRunsClick = () => {
    if (!popoverVisible) {
      loadRuns(RUNS_PAGE_SIZE).then((runs) => setRunList(runs || []));
    }
    setPopoverVisible(!popoverVisible);
  };

  const onPopoverVisibleChange = (visible?: boolean) => {
    setPopoverVisible(visible || false);
  };

  const renderSkeleton = () => (
    <div className={SKELETON_CLASS_NAME}>
      {Array.from(Array(SKELETON_COUNT).keys()).map((_, row) => (
        <Skeleton key={row} height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
      ))}
    </div>
  );

  const renderFileAttachment = (file: DataPayload, key: React.Key) => (
    <div key={key} className={[ATTACHMENTS_ITEM_CLASS_NAME, ATTACHMENTS_FILE_ITEM_CLASS_NAME].join(' ')}>
      <IbIcon iconName="paperclip" />
      <IbTypography.Paragraph type="secondary">
        <IbTooltip title={file.data.file_name}>{file.data.file_name}</IbTooltip>
      </IbTypography.Paragraph>
    </div>
  );

  const renderResourceAttachment = (resource: DataPayload, key: React.Key) => (
    <div key={key} className={[ATTACHMENTS_ITEM_CLASS_NAME, ATTACHMENTS_RESOURCE_ITEM_CLASS_NAME].join(' ')}>
      <IbIcon iconName="ad-product" />
      <IbTypography.Paragraph type="secondary">
        <IbTooltip title={resource.data.name}>{resource.data.name}</IbTooltip>
      </IbTypography.Paragraph>
    </div>
  );

  const renderRun = (run: AgentRunModel, key: React.Key) => {
    const textContent = getPayloadsTextContent(run.input);
    const resources = run.input.filter((run) => run.type === DataPayloadType.Resource);
    const files = run.input.filter((run) => run.type === DataPayloadType.File);

    return (
      <div key={key} className={RUNS_ITEM_CLASS_NAME} onClick={() => onRetry?.(run)}>
        <IbTooltip title={textContent}>{textContent}</IbTooltip>
        {(!!resources.length || !!files.length) && (
          <div className={ATTACHMENTS_CLASS_NAME}>
            {!!resources.length && resources.map(renderResourceAttachment)}
            {!!files.length && files.map(renderFileAttachment)}
          </div>
        )}
      </div>
    );
  };

  const normalizeInput = (input: DataPayload[]) =>
    JSON.stringify(
      input.map((i) => (i.type === DataPayloadType.File ? { type: i.type, title: i.data?.file_name } : i))
    );

  const filteredRuns = runList
    .filter((run, index, arr) => arr.findIndex((r) => normalizeInput(r.input) === normalizeInput(run.input)) === index)
    .filter((run) =>
      run.input.some(
        (i) =>
          i.type === dataInterface?.input.data_format ||
          (i.type === DataPayloadType.Json && dataInterface?.input.data_format === DataInterchangeFormat.Schema)
      )
    )
    .slice(0, 5);

  const renderRuns = () => <div className={RUNS_CLASS_NAME}>{filteredRuns.map(renderRun)}</div>;

  const renderPopoverContent = () => (loading ? renderSkeleton() : renderRuns());

  const onClickOutside = () => setPopoverVisible(false);
  useOnClickOutside(ref, onClickOutside, 'mouseup');

  if (!latestRun) {
    return null;
  }

  return (
    <IbPopover
      className={POPOVER_CLASS_NAME}
      content={renderPopoverContent()}
      placement="bottomLeft"
      trigger={[]}
      visible={popoverVisible}
      onVisibleChange={onPopoverVisibleChange}
    >
      <div ref={ref}>
        <IbButton
          className={MAIN_CLASS_NAME}
          disabled={loading}
          icon={<IbIcon iconName="return" />}
          loading={loading}
          type="link"
          onClick={onRetryClick}
          onExpand={onShowRunsClick}
        >
          {t('Chat: Retry request')}
        </IbButton>
      </div>
    </IbPopover>
  );
};

export default RunRetryButton;
